import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API, ym } from "@/App";
import { PlusCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { addDays, format } from "date-fns";
import BigCalendar from "@/components/calendar/BigCalendar";
import TimeAttendancePage from "./TimeAttendancePage";
import api from "@/api/axios";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

function CalendarHrPage({ userId, month, setMonth, isLeaveDialogOpen,
  setIsLeaveDialogOpen }) {
  // const [month, setMonth] = useState(ym(new Date()));
  const [data, setData] = useState({ holidays: [], leaves: [], events: [], presentUsers: [], tasks: [] });
  const [balances, setBalances] = useState(null);
  const [userTasksSummary, setUserTasksSummary] = useState([]);
  const initialForm = { type: "Casual", start_date: "", end_date: "", reason: "" };
  const [form, setForm] = useState(initialForm);

  // --- Refresh function ---
  const refresh = async () => {
    if (!userId) return;

    try {
      const [calendarRes, tasksRes, presentUsersRes, userLeaveInfo, userTaskSummary] = await Promise.all([
        api.get(`${API}/calendar`, { params: { month, user_id: userId } })
          .then((x) => x.data)
          .catch(() => ({ holidays: [], events: [], leaves: [] })),

        api.get(`${API}/tasks/calendar`, { params: { month, user_id: userId } })
          .then((x) => Array.isArray(x.data.tasks) ? x.data.tasks : [])
          .catch(() => []),

        api.get(`${API}/attendance/present-users-month`, { params: { month, userId } })
          .then((x) => Array.isArray(x.data.presentUsers) ? x.data.presentUsers : [])
          .catch(() => []),

        api.get(`${API}/calendar/user-leave-balance/${userId}`)
          .then((x) => x.data)
          .catch(() => null),
        api.get(`${API}/tasks/user-summary`, { params: { month, userId } })
          .then(x => x.data.users || [])
          .catch(() => [])
      ]);
      const normalizedHolidays = (calendarRes.holidays || []).map(h =>
        h.month ? h.month : h
      );
      const normalizedEvents = (calendarRes.events || []).map(h =>
        h.month ? h.month : h
      );
      setData({
        holidays: normalizedHolidays,
        events: normalizedEvents,
        leaves: Array.isArray(calendarRes.leaves) ? calendarRes.leaves : [],
        presentUsers: Array.isArray(presentUsersRes) ? presentUsersRes : [],
        tasks: Array.isArray(tasksRes) ? tasksRes : [],
      });
      setUserTasksSummary(userTaskSummary);
      setBalances(userLeaveInfo); // balances + requests
    } catch (error) {
      console.error("Error refreshing calendar HR data:", error);
    }
  };

  useEffect(() => { refresh(); }, [month, userId]);

  // --- Calendar events memo ---
  // const calendarEvents = useMemo(() => {
  //   const toLocalDate = (dateStr) => {
  //     if (!dateStr) return new Date();
  //     const d = new Date(dateStr);
  //     return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  //   };

  //   const holidays = (data.holidays || []).map(h => ({
  //     title: h.name,
  //     start: toLocalDate(h.date),
  //     end: toLocalDate(h.date),
  //     allDay: true,
  //     resource: { ...h, isHoliday: true },
  //   }));

  //   const events = (data.events || []).map(ev => ({
  //     title: ev.title,
  //     start: toLocalDate(ev.date),
  //     end: toLocalDate(ev.date),
  //     allDay: true,
  //     resource: { ...ev, isEvent: true },
  //   }));

  //   const leaves = (data.leaves || []).map(l => ({
  //     title: `${l.type || "Leave"} Leave`,
  //     start: toLocalDate(l.start_date),
  //     end: addDays(toLocalDate(l.end_date || l.start_date), 1),
  //     allDay: true,
  //     resource: { ...l, isLeave: true },
  //   }));

  //   const presentUsers = (data.presentUsers || []).flatMap(user =>
  //     (user.attendanceDays || []).map(day => ({
  //       title: user.name,
  //       start: toLocalDate(day.date),
  //       end: toLocalDate(day.date),
  //       allDay: true,
  //       resource: { ...day, isPresent: true, name: user.name, currentTasks: user.currentTasks || [] },
  //     }))
  //   );

  //   const taskBars = (data.presentUsers || []).flatMap(user =>
  //     (user.currentTasks || []).map(task => {
  //       if (!task.due_date) return null;
  //       return {
  //         title: task.task_title || "Untitled Task",
  //         start: toLocalDate(task.start_date || task.date || task.due_date),
  //         end: addDays(toLocalDate(task.due_date), 1),
  //         allDay: true,
  //         resource: { ...task, isTask: true, user: user.name },
  //       };
  //     }).filter(Boolean)
  //   );

  //   const tasks = (data.tasks || []).map(task => {
  //     const startDate = task.status === "Done" && task.completed_at
  //       ? toLocalDate(task.completed_at)
  //       : task.date
  //         ? toLocalDate(task.date)
  //         : task.due_date
  //           ? toLocalDate(task.due_date)
  //           : new Date();

  //     const endDate = startDate; // You can also use addDays if you want a multi-day bar

  //     return {
  //       title: task.title || task.task_title || "Untitled Task",
  //       start: startDate,
  //       end: endDate,
  //       allDay: true,
  //       resource: {
  //         ...task,
  //         isTask: true,
  //         status: task.status,
  //       },
  //     };
  //   });

  //   return [...holidays, ...events, ...leaves, ...presentUsers, ...taskBars, ...tasks];
  // }, [data]);
  // In CalendarHrPage.jsx

  const calendarEvents = useMemo(() => {
    const toLocalDate = (dateStr) => {
      if (!dateStr) return new Date();
      const d = new Date(dateStr);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };


    const holidays = data.holidays.flatMap(h =>
      (h.dates || [h.date]).map(date => ({
        title: h.name,
        start: toLocalDate(date),
        end: toLocalDate(date),
        allDay: true,
        resource: { ...h, isHoliday: true },
      }))
    );

    const events = data.events.flatMap(ev =>
      (ev.dates || [ev.date]).map(date => ({
        title: ev.title,
        start: toLocalDate(date),
        end: toLocalDate(date),
        allDay: true,
        resource: { ...ev, isEvent: true },
      })));
    const leaves = (data.leaves || []).map(l => ({
      title: `${l.type || "Leave"} Leave`,
      start: toLocalDate(l.start_date),
      end: addDays(toLocalDate(l.end_date || l.start_date), 1),
      allDay: true,
      resource: { ...l, eventType: 'leave' }, // Add eventType
    }));

    const presentUsers = (data.presentUsers || []).flatMap(user =>
      (user.attendanceDays || []).map(day => ({
        title: user.name,
        start: toLocalDate(day.date),
        end: toLocalDate(day.date),
        allDay: true,
        resource: {
          ...day,
          eventType: 'attendance', // Add eventType
          isPresent: true,
          name: user.name,
          currentTasks: user.currentTasks || []
        },
      }))
    );

    // TaskBars from presentUsers (different color)
    const taskBars = (data.presentUsers || []).flatMap(user =>
      (user.currentTasks || []).map(task => {
        if (!task.due_date) return null;
        return {
          title: task.task_title || "Untitled Task",
          start: toLocalDate(task.start_date || task.date || task.due_date),
          end: addDays(toLocalDate(task.due_date), 1),
          allDay: true,
          resource: {
            ...task,
            eventType: 'taskbar', // Different type for taskbars
            isTask: true,
            user: user.name
          },
        };
      }).filter(Boolean)
    );

    // Tasks from tasks endpoint (different color)
    const tasks = (data.tasks || []).map(task => {
      const startDate = task.status === "Done" && task.completed_at
        ? toLocalDate(task.completed_at)
        : task.date
          ? toLocalDate(task.date)
          : task.due_date
            ? toLocalDate(task.due_date)
            : new Date();

      const endDate = startDate;

      return {
        title: task.title || task.task_title || "Untitled Task",
        start: startDate,
        end: endDate,
        allDay: true,
        resource: {
          ...task,
          eventType: 'task', // Different type for tasks
          isTask: true,
          status: task.status,
        },
      };
    });

    return [...holidays, ...events, ...leaves, ...presentUsers, ...taskBars, ...tasks];
  }, [data]);

  useEffect(() => {
    console.log("Calendar events:", calendarEvents.filter(e => e.resource.eventType === "holiday"));
  }, [calendarEvents]);
  return (
    <div className="page">
      <div className="page-header">
        {/* <div>
          <h1>Calendar & Leave</h1>
          <p>Your monthly overview of events, attendance, and leave requests.</p>
        </div> */}

        {/* <div className="column gap-5">
          <div className="controls">
            <Label>Month</Label>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)} />
          </div>
        </div> */}
      </div>

      {/* <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="grid w-full my-5 grid-cols-2">
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="timesheet">My Timesheet</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <BigCalendar events={calendarEvents} month={month} />
        </TabsContent>

        <TabsContent value="timesheet">
          <TimeAttendancePage month={month} setMonth={setMonth} userId={userId} />
        </TabsContent>
      </Tabs> */}

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="grid w-full my-5 grid-cols-3">
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="timesheet">Timesheet</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <BigCalendar events={calendarEvents} month={month} userTasksSummary={userTasksSummary} />
        </TabsContent>

        <TabsContent value="timesheet">
          <TimeAttendancePage month={month} setMonth={setMonth} userId={userId} />
        </TabsContent>

        <TabsContent value="leave">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Leave Balances & Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {balances ? (
                <>
                  {/* Leave balances */}
                  <div className="flex flex-wrap gap-4 p-4 border rounded-lg bg-muted/50 mb-6">
                    {Object.entries(balances.leaveBalance || {}).map(([type, days]) => (
                      <div key={type} className="text-center">
                        <div className="text-2xl font-bold">{days}</div>
                        <div className="text-sm text-muted-foreground capitalize">{type} Days</div>
                      </div>
                    ))}
                  </div>

                  {/* Leave requests */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Manager Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(balances.leaveRequests || []).map((req) => (
                        <TableRow key={req.id}>
                          <TableCell>{req.type}</TableCell>
                          <TableCell>{format(new Date(req.start_date), "dd MMM yyyy")}</TableCell>
                          <TableCell>{format(new Date(req.end_date), "dd MMM yyyy")}</TableCell>
                          <TableCell>{req.reason}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                req.status === "APPROVED"
                                  ? "success"
                                  : req.status === "REJECTED"
                                    ? "destructive"
                                    : "secondary"
                              }
                            >
                              {req.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {req.approval_justification || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  No leave information available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}

export default CalendarHrPage;
